import React, { createContext, useContext, useReducer, ReactNode } from "react";
// import { StoryNavigationNode } from "common/build/api-parameters/story";

// Navigation state interface following design guide patterns
interface NavigationState {
    currentPart: number;
    visitedParts: number[];
    availablePaths: Array<{ targetPartNumber: number; choiceText: string }>;
    pathHistory: Array<{ from: number; to: number; choice: string }>;
}

// Action types for reducer pattern (following react-tracked approach)
type NavigationAction =
    | { type: "NAVIGATE_TO_PART"; payload: { partNumber: number; choice?: string } }
    | { type: "RESET_NAVIGATION" }
    | { type: "NAVIGATE_BACK" }
    | { type: "SET_AVAILABLE_PATHS"; payload: Array<{ targetPartNumber: number; choiceText: string }> }
    | { type: "SET_CURRENT_PART"; payload: number };

// Context interface
interface BranchedStoryContextType {
    navigationState: NavigationState;
    navigateToPart: (partNumber: number, choice?: string) => void;
    navigateBack: () => void;
    resetNavigation: () => void;
    setAvailablePaths: (paths: Array<{ targetPartNumber: number; choiceText: string }>) => void;
    setCurrentPart: (partNumber: number) => void;
}

// Initial state following design guide defaults
const initialState: NavigationState = {
    currentPart: 1,
    visitedParts: [1],
    availablePaths: [],
    pathHistory: [],
};

// Reducer function with comprehensive action handling
const navigationReducer = (state: NavigationState, action: NavigationAction): NavigationState => {
    switch (action.type) {
        case "NAVIGATE_TO_PART":
            return {
                ...state,
                currentPart: action.payload.partNumber,
                visitedParts: [...new Set([...state.visitedParts, action.payload.partNumber])],
                pathHistory: action.payload.choice
                    ? [
                          ...state.pathHistory,
                          {
                              from: state.currentPart,
                              to: action.payload.partNumber,
                              choice: action.payload.choice,
                          },
                      ]
                    : state.pathHistory,
            };

        case "NAVIGATE_BACK": {
            if (state.visitedParts.length <= 1) {
                return state; // Cannot go back from the first part
            }
            const newVisitedParts = state.visitedParts.slice(0, -1);
            const newPathHistory = state.pathHistory.slice(0, -1);
            const newCurrentPart = newVisitedParts[newVisitedParts.length - 1];

            return {
                ...state,
                currentPart: newCurrentPart,
                visitedParts: newVisitedParts,
                pathHistory: newPathHistory,
            };
        }

        case "RESET_NAVIGATION":
            return { ...initialState };

        case "SET_AVAILABLE_PATHS":
            return {
                ...state,
                availablePaths: action.payload,
            };

        case "SET_CURRENT_PART":
            return {
                ...state,
                currentPart: action.payload,
                visitedParts: [...new Set([...state.visitedParts, action.payload])],
            };

        default:
            return state;
    }
};

// Create context with null default (will throw error if used without provider)
const BranchedStoryContext = createContext<BranchedStoryContextType | null>(null);

/**
 * BranchedStoryProvider Component
 *
 * Provides navigation state management for branched story components.
 * Follows react-tracked pattern used throughout the application.
 *
 * @param children - React children to wrap with provider
 *
 * @example
 * <BranchedStoryProvider>
 *   <BranchedStoryComponent />
 * </BranchedStoryProvider>
 */
export const BranchedStoryProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [navigationState, dispatch] = useReducer(navigationReducer, initialState);

    // Action creators following design guide callback patterns
    const navigateToPart = React.useCallback((partNumber: number, choice?: string) => {
        dispatch({ type: "NAVIGATE_TO_PART", payload: { partNumber, choice } });
    }, []);

    const navigateBack = React.useCallback(() => {
        dispatch({ type: "NAVIGATE_BACK" });
    }, []);

    const resetNavigation = React.useCallback(() => {
        dispatch({ type: "RESET_NAVIGATION" });
    }, []);

    const setAvailablePaths = React.useCallback((paths: Array<{ targetPartNumber: number; choiceText: string }>) => {
        dispatch({ type: "SET_AVAILABLE_PATHS", payload: paths });
    }, []);

    const setCurrentPart = React.useCallback((partNumber: number) => {
        dispatch({ type: "SET_CURRENT_PART", payload: partNumber });
    }, []);

    // Memoized context value to prevent unnecessary re-renders
    const contextValue = React.useMemo(
        () => ({
            navigationState,
            navigateToPart,
            navigateBack,
            resetNavigation,
            setAvailablePaths,
            setCurrentPart,
        }),
        [navigationState, navigateToPart, navigateBack, resetNavigation, setAvailablePaths, setCurrentPart],
    );

    return <BranchedStoryContext.Provider value={contextValue}>{children}</BranchedStoryContext.Provider>;
};

/**
 * Custom hook to access branched story navigation state
 *
 * Must be used within a BranchedStoryProvider.
 * Provides navigation state and action creators.
 *
 * @returns Navigation state and action functions
 * @throws Error if used outside of BranchedStoryProvider
 *
 * @example
 * const { navigationState, navigateToPart, resetNavigation } = useBranchedStoryNavigation();
 */
export const useBranchedStoryNavigation = (): BranchedStoryContextType => {
    const context = useContext(BranchedStoryContext);

    if (!context) {
        throw new Error(
            "useBranchedStoryNavigation must be used within BranchedStoryProvider. " +
                "Wrap your component tree with <BranchedStoryProvider>.",
        );
    }

    return context;
};

// Display names for debugging (following design guide)
BranchedStoryProvider.displayName = "BranchedStoryProvider";

// Export types for use in other components
export type { NavigationState, NavigationAction, BranchedStoryContextType };
